import { useEffect, type ReactNode } from "react";
import { setLocale, getLocale, locales } from "../paraglide/runtime.js";

/**
 * LocaleProvider — applies the active Paraglide locale to <html lang dir>.
 *
 * Paraglide's `getLocale()` reads from the cookie-backed strategy; we
 * mirror it onto the document so RTL works (Arabic) and assistive tech
 * sees the right language. The default strategy persists locale changes
 * via a cookie, so this only needs to run once on mount.
 *
 * Locale switching is exposed via `setLocale()` from "@/paraglide/runtime"
 * — the provider re-reads on every render so a route-level switcher
 * picks up changes without a hard reload.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const current = getLocale();

  useEffect(() => {
    const html = document.documentElement;
    html.lang = current;
    html.dir = current === "ar" ? "rtl" : "ltr";
  }, [current]);

  return <>{children}</>;
}

/**
 * Convenience: cycle to the next available locale. Wired to a button in
 * the future (header or settings) once the desktop shell ports in 3.8.
 */
export function toggleLocale(): void {
  const all = locales as readonly string[];
  const i = all.indexOf(getLocale());
  const next = all[(i + 1) % all.length] ?? "en";
  setLocale(next as never);
}
